// =============================================================================
// Hosted-agent links (B3 — HERMES wave 2, configuration surface only)
//
// A link is a DIRECTED edge A→B in the tenant's AgentLink register (plan B4:
// all agent-to-agent traffic is brokered by the backend; default = no
// communication). The edge lives in the FROM agent's document (`links[]`),
// like routines, so it inherits the agent's revision/ETag. Wave 3 turns the
// register into runtime execution; nothing here executes anything.
//
// Edge identity (plan B5): `caller` (default) means B answers filtered by the
// end user's own permissions. `agent` is the permission BRIDGE — B answers with
// its own access — and is an explicit opt-in the server guards with
// `confirm_bridge: true` (400 `bridge_confirmation_required` otherwise).
// =============================================================================

import axios from "axios"

import { api } from "@/lib/api-client"

/** Wave 3 may add "delegate"/"handoff"; wave 2 only carries "ask". */
export type HostedAgentLinkMode = "ask"

export type HostedAgentLinkIdentity = "caller" | "agent"

export type HostedAgentLink = {
  /** "link:<uuid>" — server-stamped. */
  id: string
  /** Owner document; always the canonical "agent:<uuid>", never a slug. */
  from_agent_id: string
  to_agent_id: string
  mode: HostedAgentLinkMode
  identity: HostedAgentLinkIdentity
  /** Default false (Grok pattern, like routines): a drawn link starts off. */
  enabled: boolean
  created_by?: string
  created_at?: string
  updated_at?: string
}

export type HostedAgentLinkCreateInput = {
  to_agent_id: string
  mode?: HostedAgentLinkMode
  identity?: HostedAgentLinkIdentity
  enabled?: boolean
  /** Required (true) whenever `identity` is "agent". */
  confirm_bridge?: boolean
}

export type HostedAgentLinkPatch = {
  mode?: HostedAgentLinkMode
  identity?: HostedAgentLinkIdentity
  enabled?: boolean
  confirm_bridge?: boolean
}

export type HostedAgentLinkErrorCode =
  | "bridge_confirmation_required"
  | "link_target_not_found"
  | "link_exists"
  | "self_link"
  | "revision_conflict"

/**
 * Rethrow 400/404/409 responses that carry a stable link error code as an
 * Error whose `cause` is `{ code }` so the UI can pick a translated message.
 */
function rethrowLinkError(error: unknown): never {
  if (axios.isAxiosError(error) && error.response) {
    const data = error.response.data as { code?: string; error?: string } | undefined
    const code = data?.code
    if (
      code === "bridge_confirmation_required" ||
      code === "link_target_not_found" ||
      code === "link_exists" ||
      code === "self_link" ||
      code === "revision_conflict"
    ) {
      throw new Error(`${code}: ${data?.error ?? "hosted agent link request rejected"}`, {
        cause: { code },
      })
    }
  }
  throw error
}

/** Narrow an unknown error to a link error code, if it is one. */
export function getHostedAgentLinkErrorCode(error: unknown): HostedAgentLinkErrorCode | null {
  if (error instanceof Error && typeof error.cause === "object" && error.cause !== null) {
    const code = (error.cause as { code?: unknown }).code
    if (
      code === "bridge_confirmation_required" ||
      code === "link_target_not_found" ||
      code === "link_exists" ||
      code === "self_link" ||
      code === "revision_conflict"
    ) {
      return code
    }
  }
  return null
}

/**
 * Every link in the tenant (aggregated server-side from all agents'
 * `links[]`) — one request for the whole map.
 */
export async function listHostedAgentLinks(): Promise<HostedAgentLink[]> {
  const response = await api.get("/admin/hosted-agents/links")
  const data = response.data || response
  return Array.isArray(data?.links) ? (data.links as HostedAgentLink[]) : []
}

export async function createHostedAgentLink(
  fromAgentId: string,
  input: HostedAgentLinkCreateInput
): Promise<HostedAgentLink> {
  try {
    const response = await api.post(
      `/admin/hosted-agents/${encodeURIComponent(fromAgentId)}/links`,
      input
    )
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to create agent link")
    }
    return data.link as HostedAgentLink
  } catch (error) {
    rethrowLinkError(error)
  }
}

export async function updateHostedAgentLink(
  fromAgentId: string,
  linkId: string,
  patch: HostedAgentLinkPatch
): Promise<HostedAgentLink> {
  try {
    const response = await api.put(
      `/admin/hosted-agents/${encodeURIComponent(fromAgentId)}/links/${encodeURIComponent(linkId)}`,
      patch
    )
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to update agent link")
    }
    return data.link as HostedAgentLink
  } catch (error) {
    rethrowLinkError(error)
  }
}

export async function deleteHostedAgentLink(fromAgentId: string, linkId: string): Promise<void> {
  try {
    const response = await api.delete(
      `/admin/hosted-agents/${encodeURIComponent(fromAgentId)}/links/${encodeURIComponent(linkId)}`
    )
    const data = response.data || response
    if (data?.success === false) {
      throw new Error(data.message || data.error || "Failed to delete agent link")
    }
  } catch (error) {
    rethrowLinkError(error)
  }
}
